import { Paper } from '@mantine/core';
import React, { useState, useRef, useCallback } from 'react';

import IconChevronDown from '~/domains/design-system/icons/IconChevronDown';
import { Title } from '~/domains/design-system/Title';

import useStyles from './Dropdown.styles';
import type { DropdownProps } from './types';

export function Dropdown({
  options,
  value,
  onChange,
  className,
  disableInvertedColors,
  expandMenuWidth = false,
  menuMaxHeight,
  fullWidth = false,
}: DropdownProps) {
  const { classes, cx } = useStyles();
  const [open, setOpen] = useState(false);
  const [focusedIndex, setFocusedIndex] = useState<number>(-1);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (!open) {
        if (e.key === 'Enter' || e.key === ' ' || e.key === 'ArrowDown') {
          e.preventDefault();
          setOpen(true);
          setFocusedIndex(0);
        }
        return;
      }

      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          setFocusedIndex((prev) => (prev + 1) % options.length);
          break;
        case 'ArrowUp':
          e.preventDefault();
          setFocusedIndex((prev) => (prev - 1 + options.length) % options.length);
          break;
        case 'Enter':
        case ' ':
          e.preventDefault();
          if (focusedIndex >= 0) {
            onChange(options[focusedIndex].value);
            setOpen(false);
            buttonRef.current?.focus();
          }
          break;
        case 'Escape':
          e.preventDefault();
          setOpen(false);
          buttonRef.current?.focus();
          break;
        case 'Tab':
          setOpen(false);
          break;
        default:
          break;
      }
    },
    [open, focusedIndex, options, onChange]
  );

  const handleOptionKeyDown = (optValue: string) => (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onChange(optValue);
      setOpen(false);
      buttonRef.current?.focus();
    }
  };

  const selectedOption = options.find((opt) => opt.value === value);
  const displayValue = selectedOption?.buttonLabel || selectedOption?.label || '';
  const isFirstOption = options[0]?.value === value;
  const shouldInvertColors = !disableInvertedColors && !isFirstOption;

  return (
    <div className={cx(classes.root, { [classes.rootFullWidth]: fullWidth })}>
      <button
        ref={buttonRef}
        className={cx(
          classes.button,
          { [classes.buttonInverted]: shouldInvertColors, [classes.buttonFullWidth]: fullWidth },
          className
        )}
        onClick={() => setOpen((o) => !o)}
        onKeyDown={handleKeyDown}
        aria-haspopup="listbox"
        aria-expanded={open}
        type="button"
      >
        <span style={{ flex: 1, textAlign: 'left' }}>{displayValue}</span>
        <span className={cx(classes.chevron, { [classes.chevronOpen]: open })}>
          <IconChevronDown size={16} color={shouldInvertColors ? '#FFF' : undefined} />
        </span>
      </button>
      {open && (
        <Paper
          ref={menuRef}
          className={cx(classes.menu, {
            [classes.menuExpanded]: expandMenuWidth,
            [classes.menuFullWidth]: fullWidth,
          })}
          style={menuMaxHeight ? { maxHeight: menuMaxHeight, overflowY: 'auto' } : undefined}
          withBorder={false}
          role="listbox"
        >
          {options.map((opt, index) => (
            <div
              key={opt.value}
              className={cx(classes.option, {
                [classes.selected]: value === opt.value,
                [classes.focused]: index === focusedIndex,
              })}
              onClick={() => {
                onChange(opt.value);
                setOpen(false);
                buttonRef.current?.focus();
              }}
              onKeyDown={handleOptionKeyDown(opt.value)}
              role="option"
              aria-selected={value === opt.value}
              tabIndex={0}
            >
              <span
                className={cx(classes.customRadio, {
                  [classes.customRadioChecked]: value === opt.value,
                })}
                aria-hidden="true"
              />
              <Title
                component="span"
                className={cx(classes.optionLabel, {
                  [classes.optionLabelExpanded]: expandMenuWidth || fullWidth,
                })}
                variant="title-small"
              >
                {opt.label}
              </Title>
            </div>
          ))}
        </Paper>
      )}
    </div>
  );
}

Dropdown.displayName = 'Dropdown';
